<template>
  <div>
    <div v-if="!loadingError && !isLoading" class="evaluation-header">
      <div class="header-name">
        <h2>
          {{ evaluation? `${$t('evaluationTitle')} de ${setValueToFormatted(evaluation.idNumber, 'idNumber')}` :
            'No se encontró la comprobación' }}
        </h2>
      </div>
      <div class="header-buttons">
        <b-button
          id="risk-evaluations-return-button"
          class="is-button-primary ml-2 is-small is-rounded is-pulled-right"
          @click="returnToRiskEvaluation()">
          {{ $t('backButton') }}
        </b-button>
        <b-dropdown aria-role="list">
          <template #trigger="{ active }">
            <b-button
              id="risk-evaluations-indicators"
              type="is-primary is-light"
              size="is-small"
              :label="`Ver indicadores`"
              rounded
              :icon-right="active ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
              @submit.prevent="" />
          </template>

          <b-dropdown-item id="risk-evaluations-indicators-uf" aria-role="listitem">
            {{ `UF:
          ${setValueToFormatted(indicators.uf, 'currencyNumber')}` }}
          </b-dropdown-item>
          <b-dropdown-item id="risk-evaluations-indicators-utm" aria-role="listitem">
            {{ `UTM:
          ${setValueToFormatted(indicators.utm, 'currencyNumber')}` }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    <Spinner v-if="isLoading" data-cy="spinner" :is-loading="isLoading" />
    <Message v-else-if="!!loadingError && !isLoading" :message="loadingError.message" :icon="loadingError.icon" />
    <div v-else-if="!calculated" class="card-item evaluation-body">
      <Debtors
        v-model="inputs.debtors"
        :extended-debtor="extendedDebtor"
        :input-type="inputType"
        :feature-toggle-eligibility-button="featureToggleEligibilityButton"
        :equifax-pdf-link="externalProviderDataRequest.equifaxPdfLink"
        @delete-codebtor="deleteCodebtor($event)"
        @set-extended-debtor="setExtendedDebtor($event)"
        @add-input="addInput($event)"
        @update-input="updateInput($event)"
        @delete-input="deleteInput($event)"
        @save-changed-id-number="saveChangedIdNumber($event)" />
      <b-button
        id="new-codebtor"
        class="add-codebtor is-button-secondary is-button-second mt-3 mb-3"
        rounded
        @click="addCodebtor()">
        Agregar codeudor
      </b-button>
      <div class="options">
        <b-button id="calculate" class="button is-button-fourth is-rounded" @click="saveDepuration()">
          <span>{{ $t('refreshButton') }}</span>
        </b-button>
      </div>
    </div>
    <div v-if="calculated" class="card-item evaluation-body">
      <div class="details-card">
        <div class="details-header">
          <span class="content-title">{{ $t('summary') }}</span>
        </div>
        <div class="card-item-details">
          <div class="details-header">
            <span>
              <span class="has-text-weight-bold">{{ $t('ufText') }}</span>
              <span id="uf-value">{{ setValueToFormatted(summary.uf, 'currencyNumber') }} </span>
            </span>
            <span>
              <span class="has-text-weight-bold">{{ $t('utmText') }} </span>
              <span id="utm-value">{{ setValueToFormatted(summary.utm, 'currencyNumber') }} </span>
            </span>
          </div>
          <div v-for="debtor in summary.debtors" :key="debtor.debtorIndex">
            <div class="details-header">
              <span :id="`debtor-${debtor.debtorIndex}-incomeSummary-title`" class="has-text-weight-bold">
                {{ $t('incomeSummary') }} {{
                  debtor.type === 'DEBTOR' ? $t('debtor') : `${$t('codebtor')} ${debtor.debtorIndex}`
                }} {{ $t('money') }}:
              </span>
              <span :id="`debtor-${debtor.debtorIndex}-incomeSummary`" class="title-text">
                {{ debtor.debtorIncome }}
              </span>
            </div>
            <div class="details-header">
              <span :id="`debtor-${debtor.debtorIndex}-debtSummary-title`" class="has-text-weight-bold">
                {{ $t('debtSummary') }} {{
                  debtor.type === 'DEBTOR' ? $t('debtor') : `${$t('codebtor')} ${debtor.debtorIndex}`
                }} {{ $t('money') }}:
              </span>
              <span :id="`debtor-${debtor.debtorIndex}-debtSummary`" class="title-text">
                {{ debtor.debtorDebt }}
              </span>
            </div>
          </div>
          <div class="details-header">
            <span id="income-summary-percentage-fixed-title" class="has-text-weight-bold">
              {{ $t('incomeSummaryPercentageFixed') }}
            </span>
            <span id="income-summary-percentage-fixed" class="title-text">
              {{ setValueToFormatted(summary.totalFixed, 'currencyNumber') }}
            </span>
          </div>
          <div class="details-header">
            <span id="income-summary-percentage-variable-title" class="has-text-weight-bold">
              {{ $t('incomeSummaryPercentageVariable') }}
            </span>
            <span id="income-summary-percentage-variable" class="title-text">
              {{ setValueToFormatted(summary.totalVariable, 'currencyNumber') }}
            </span>
          </div>
          <div class="details-header">
            <span id="income-summary-total-title" class="has-text-weight-bold">
              {{ $t('incomeSummaryTotal') }}
            </span>
            <span id="income-summary-total" class="title-text">
              {{ setValueToFormatted(summary.totalIncome, 'currencyNumber') }}
            </span>
            <span id="total-income-uf-title" class="has-text-weight-bold">
              {{ $t('incomeSummaryTotalUF') }}
            </span>
            <span id="total-income-uf" class="title-text">
              {{ setValueToFormatted(summary.totalIncomeUf, 'currencyNumber') }}
            </span>
          </div>
          <div class="details-header">
            <span id="debt-summary-total-title" class="has-text-weight-bold">
              {{ $t('debtSummaryTotal') }}
            </span>
            <span id="debt-summary-total" class="title-text">
              {{ summary.totalDebt }}
            </span>
          </div>
          <div v-if="featureToggleEligibilityButton">
            <div v-if="summary.eligibilityResult" class="details-header">
              <div>
                <span
                  id="eligibility-summary-total-title"
                  class="has-text-weight-bold">
                  {{ $t('eligibilitySummary') }}
                </span>
                <span id="eligibility-summary-total" class="title-text">
                  {{ $t('trueEligibilitySummary') }}
                </span>
              </div>
              <div class="eligibility-summary-reasons">
                <span
                  id="eligibility-summary-detail-title"
                  class="has-text-weight-bold">
                  {{ $t('summaryReasons') }}
                </span>
                <span
                  v-for="(eligibility, index) in summary.detailEligibility"
                  :key="index"
                  class="title-text">
                  <li :id="`eligibility-summary-detail-${index}`">
                    {{ Object.values(eligibility)[0] }}
                  </li>
                </span>
              </div>
            </div>
            <div v-else>
              <div class="details-header">
                <span
                  id="eligibility-summary-total-title"
                  class="has-text-weight-bold">
                  {{ $t('eligibilitySummary') }}
                </span>
                <span
                  id="eligibility-summary-total"
                  class="title-text">
                  {{ $t('falseEligibilitySummary') }}
                </span>
              </div>
              <div>
                <span
                  id="eligibility-summary-detail-title"
                  class="has-text-weight-bold">
                  {{ $t('summaryReasons') }}
                </span>
                <span
                  v-for="(eligibility, index) in summary.detailEligibility"
                  :key="index"
                  class="title-text">
                  <li :id="`eligibility-summary-detail-${index}`">
                    {{ Object.values(eligibility)[0] }}
                  </li>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="options">
          <button id="download-button" class="button is-button-primary is-rounded" @click="getDocument()">
            <span>{{ $t('downloadButton') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import Debtors from '@/ui/components/internal/risk-evaluation/Debtors.vue';
import notification from '@/ui/mixins/notification';
import Message from '@/ui/components/Message.vue';
import Spinner from '@/ui/components/Spinner.vue';
import { getRiskEvaluationById, calculateRiskEvaluation, patchRiskEvaluation, getRiskEvaluationDocument, getEligibilityData } from '@/application/services/api-clients/risk-evaluations';
import { valueToFormatted } from 'creditu-types/format';
import { getUtm, getUf } from '@/application/services/api-clients/indicators';
import { getFeatureToggleByName } from '@/application/services/api-clients/features-toggles';

export default ({
  name: 'Evaluate',
  components: { Debtors, Message, Spinner },
  mixins: [notification],
  data() {
    return {
      featureToggleEligibilityButton: false,
      evaluationId: this.$router.history.current.params.evaluationId,
      indicators: {},
      evaluation: null,
      inputs: null,
      inputType: null,
      extendedDebtor: null,
      isLoading: true,
      loadingError: null,
      calculated: false,
      externalProviderDataRequest: {},
      eligibilityData: null,
      prospectIdNumber: '',
      summary: {},
      htmlToPdfOptions: {
        margin: 0
      },
      baseInputs: {
        fixedIncomes: { description: '', amounts: null },
        variableNonPeriodicIncomes: { description: '', amounts: [null, null, null] },
        variablePeriodicIncomes: { description: '', amounts: null, periodicity: null },
        nonTaxableIncomes: { description: '', amounts: [null, null, null] },
        otherDiscounts: { description: '', amounts: null },
        otherIncomes: { description: '', amounts: null },
        accreditedDebts: { description: '', balance: null, termType: null, monthlyPayment: null }
      }
    };
  },
  computed: {
    checkOutput() {
      if (!this.evaluation.outputs || this.evaluation.outputs === undefined || this.evaluation.outputs.length === 0) return true;
      return false;
    }
  },
  async mounted() {
    this.isLoading = true;
    await this.getFeatureToggleEligibilityButton();
    await this.loadData();
    this.isLoading = false;
  },
  methods: {
    depurateEligibilityData(data) {
      this.eligibilityData = null;
      const { equifaxProspectInformation, equifaxDocumentId } = data || {};
      if (!equifaxProspectInformation || !equifaxDocumentId) {
        return this.eligibilityData;
      }
      this.eligibilityData = {
        documentId: equifaxDocumentId,
        age: equifaxProspectInformation.edad,
        nationalityOrResidence: equifaxProspectInformation.nacionalidad,
        defaultingDebtsOrProtests: equifaxProspectInformation.cantidadMoras || equifaxProspectInformation.cantidadProtestos ? 'Posee' : 'No posee'
      };
      return this.eligibilityData;
    },
    eligibilityModelBuilder(depuratedData, criteria) {
      let model = {
        forcedValue: false,
        inputOrigin: 'USER_SWITCH',
        comment: ''
      };
      if (!criteria) {
        return model;
      }
      if (depuratedData !== null) {
        model = {
          value: depuratedData[criteria],
          inputOrigin: 'EXTERNAL_PROVIDER',
          externalProviderId: depuratedData.documentId,
          forcedValue: false,
          comment: ''
        };
        return model;
      }
      return model;
    },
    async getFeatureToggleEligibilityButton() {
      const { isActive } = await getFeatureToggleByName('risk-evaluation-eligibility-button');
      this.featureToggleEligibilityButton = isActive;
    },
    async loadData() {
      let evaluation;
      if (this.evaluationId !== 'test') {
        try {
          // Esto es retomar
          evaluation = await getRiskEvaluationById(this.evaluationId);
          this.externalProviderDataRequest = await getEligibilityData(evaluation.idNumber);
          this.depurateEligibilityData(this.externalProviderDataRequest);
          delete evaluation.outputs;
        } catch (_error) {
          evaluation = null;

          this.loadingError = {
            message: 'Hubo un error al tratar de comunicarnos con el servidor',
            icon: 'data_maintenance.svg'
          };
        }
      }
      const testEvaluation = {
        idNumber: 'test',
        debtors: [
          {
            debtorIndex: 0,
            type: 'DEBTOR',
            debt: {
              longTermDebt: 0, // saldo insoluto según SBIF
              shortTermDebt: 0, // saldo insoluto según SBIG
              accreditedDebts: []
            },
            income: {
              salaryStatements: [
                {
                  fixedIncomes: [],
                  variableNonPeriodicIncomes: [],
                  variablePeriodicIncomes: [],
                  nonTaxableIncomes: [],
                  otherDiscounts: []
                }
              ],
              otherIncomes: []
            },
            eligibility: {
              age: this.eligibilityModelBuilder(this.eligibilityData, 'age'),
              nationalityOrResidence: this.eligibilityModelBuilder(this.eligibilityData, 'nationalityOrResidence'),
              defaultingDebtsOrProtests: this.eligibilityModelBuilder(this.eligibilityData, 'defaultingDebtsOrProtests'),
              financialCourtCases: this.eligibilityModelBuilder(this.eligibilityData),
              sixMonthsAntiquity: this.eligibilityModelBuilder(this.eligibilityData),
              twelveMonthsEmploymentContinuity: this.eligibilityModelBuilder(this.eligibilityData)
            }
          }
        ],
        outputs: null
      };
      if (evaluation && evaluation.debtors && evaluation.debtors.length === 0) {
        evaluation.debtors = testEvaluation.debtors;
      }

      if (evaluation !== null) {
        this.updateInputs(evaluation || testEvaluation);
      }

      if (!this.$router.history.current.params.indicators) {
        const utm = await getUtm();
        const uf = await getUf();
        this.indicators = { utm, uf };
      } else {
        this.indicators = this.$router.history.current.params.indicators;
      }
    },
    setValueToFormatted(value, type) {
      if (value === 'test' && type === 'idNumber') {
        return value;
      }
      return valueToFormatted(type, this.$i18n.locale, value);
    },
    setExtendedDebtor({ debtorIndex, inputType }) {
      if (this.extendedDebtor === debtorIndex && this.inputType === inputType) {
        this.extendedDebtor = null;
        this.inputType = null;
      } else {
        this.extendedDebtor = debtorIndex;
        this.inputType = inputType;
      }
    },
    updateInputs(evaluation) {
      this.evaluation = evaluation;
      this.inputs = evaluation;
    },
    returnToRiskEvaluation() {
      if (this.calculated) {
        this.calculated = !this.calculated;
      } else {
        this.$router.push({ name: 'risk-evaluation' });
      }
    },
    addCodebtor() {
      const newCodebtor = {
        debtorIndex: this.inputs.debtors.length,
        type: 'CODEBTOR',
        debt: {
          longTermDebt: 0,
          shortTermDebt: 0,
          accreditedDebts: []
        },
        income: {
          salaryStatements: [
            {
              fixedIncomes: [],
              variableNonPeriodicIncomes: [],
              variablePeriodicIncomes: [],
              nonTaxableIncomes: [],
              otherDiscounts: []
            }
          ],
          otherIncomes: []
        },
        eligibility: {
          age: this.eligibilityModelBuilder(this.eligibilityData),
          nationalityOrResidence: this.eligibilityModelBuilder(this.eligibilityData),
          defaultingDebtsOrProtests: this.eligibilityModelBuilder(this.eligibilityData),
          financialCourtCases: this.eligibilityModelBuilder(this.eligibilityData),
          sixMonthsAntiquity: this.eligibilityModelBuilder(this.eligibilityData),
          twelveMonthsEmploymentContinuity: this.eligibilityModelBuilder(this.eligibilityData)
        }
      };
      this.inputs.debtors.push(newCodebtor);
    },
    deleteCodebtor(index) {
      this.inputs.debtors.splice(index, 1);
      this.extendedDebtor = null;
      this.inputType = null;
      this.refreshDebtorsIndexes();
    },
    refreshDebtorsIndexes() {
      for (let i = 0; i < this.inputs.debtors.length; i += 1) {
        this.inputs.debtors[i].debtorIndex = i;
      }
    },
    addInput({ debtorIndex, type, gloss }) {
      if (type === 'debt' || gloss === 'otherIncomes') {
        this.inputs.debtors[debtorIndex][type][gloss].push(this.baseInputs[gloss]);
      } else {
        this.inputs.debtors[debtorIndex][type].salaryStatements[0][gloss].push(this.baseInputs[gloss]);
      }
    },
    updateInput({ debtorIndex, type, incomeType, incomeIndex, gloss, inputIndex, field, value }) {
      if (type === 'debt') {
        if (['shortTermDebt', 'longTermDebt'].includes(gloss)) {
          this.inputs.debtors[debtorIndex][type][gloss] = value;
        } else {
          this.inputs.debtors[debtorIndex][type][gloss][inputIndex][field] = value;
        }
      } else if (type === 'income') {
        this.inputs.debtors[debtorIndex][type][incomeType][incomeIndex][gloss][inputIndex][field] = value;
      } else if (type === 'eligibility') {
        this.inputs.debtors[debtorIndex][type][gloss][inputIndex].forcedValue = value;
        // this.inputs.debtors[debtorIndex][type][gloss][inputIndex].comment = value;
      }
    },
    deleteInput({ debtorIndex, type, incomeIndex, gloss, inputIndex }) {
      if (type === 'debt') {
        this.inputs.debtors[debtorIndex][type][gloss].splice(inputIndex, 1);
        // type == 'income'
      } else if (gloss === 'otherIncomes') {
        this.inputs.debtors[debtorIndex][type][gloss].splice(inputIndex, 1);
      } else { // inside salaryStatements
        this.inputs.debtors[debtorIndex][type].salaryStatements[incomeIndex][gloss].splice(inputIndex, 1);
      }
    },
    async saveChangedIdNumber({ debtorIndex, idNumber }) {
      this.evaluation.debtors[debtorIndex] = this.evaluation.debtors[debtorIndex] || {};
      this.evaluation.debtors[debtorIndex].idNumber = idNumber;
      if (this.evaluationId !== 'test') {
        await patchRiskEvaluation(this.evaluation);
      }
    },
    getSummary() {
      this.calculated = true;
      const debtors = [];
      const totalDebt = this.evaluation.outputs.reduce((acc, debtor) => {
        return acc + debtor.debt.monthlyDebt;
      }, 0);
      this.evaluation.debtors.forEach(debtor => {
        const debtorOutputs = this.evaluation.outputs.find(output => output.debtorIndex === debtor.debtorIndex);
        debtors.push({
          debtorIndex: debtor.debtorIndex,
          type: debtor.type,
          debtorIncome: this.setValueToFormatted(debtorOutputs.income.monthlyNetIncome || 0, 'currencyNumber'),
          debtorDebt: this.setValueToFormatted(debtorOutputs.debt.MonthlyDebt || 0, 'currencyNumber')
        });
      });
      this.summary = this.evaluation.summary || {};
      this.summary.debtors = debtors;
      this.summary.totalDebt = this.setValueToFormatted(totalDebt || 0, 'currencyNumber');
    },
    async saveDepuration() {
      try {
        // Fix: El periodo debe ser entero, no un string. TODO: arreglar los inputs y quitar esto.
        this.evaluation = this.inputs;
        this.evaluation.debtors.forEach(debtor => {
          debtor.income.salaryStatements.forEach(salaryStatement => {
          // eslint-disable-next-line no-param-reassign
            salaryStatement.fixedIncomes.forEach(fixedIncome => {
              if (typeof fixedIncome.amounts === 'number') {
              // eslint-disable-next-line no-param-reassign
                fixedIncome.amounts = [fixedIncome.amounts];
              }
            });
            salaryStatement.variablePeriodicIncomes.forEach(variablePeriodicIncome => {
            // eslint-disable-next-line no-param-reassign
              variablePeriodicIncome.periodicity = parseInt(variablePeriodicIncome.periodicity, 10);
              if (typeof variablePeriodicIncome.amounts === 'number') {
              // eslint-disable-next-line no-param-reassign
                variablePeriodicIncome.amounts = [variablePeriodicIncome.amounts];
              }
            });
          });
        });

        this.evaluation = await calculateRiskEvaluation(this.evaluation);
        if (this.evaluationId !== 'test') {
          await patchRiskEvaluation(this.evaluation);
        }
        this.getSummary();
      } catch (_error) {
        this.alert({ message: 'Oops... Hubo un error en la solicitud, verifica que todos los campos han sido llenados correctamente' });
      }
    },
    async getDocument() {
      try {
        const body = this.evaluation.idNumber === 'test' ? this.evaluation : {};
        const { pdfBase64 } = await getRiskEvaluationDocument(this.evaluation.idNumber, body);
        if (pdfBase64) {
          const linkSource = `data:application/pdf;base64,${pdfBase64}`;
          const fileName = `depuracion-${this.evaluation.idNumber}.pdf`;

          const downloadLink = document.createElement('a');
          downloadLink.style = 'display: none';
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      } catch (_e) {
        this.alert({ message: 'Hubo un error al tratar de generar el documento' });
      }
    }
  }
});
</script>

<style scoped>
.evaluation-header {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 0rem 1rem;
  place-items: center;
  color: var(--primary);
  font-weight: 600;
}

.evaluation-header .header-name {
  display: flex;
  justify-self: start;
  align-items: center;
}

.evaluation-header .header-buttons {
  display: normal;
  justify-self: end;
}

.options {
  display: flex;
  justify-content: space-evenly;
  margin-top: 1.5rem;
}

.evaluation-body {
  display: flex;
  flex-flow: column wrap;
  justify-content: space-around;
  gap: 0.2rem;
}

.details-header {
  display: flex;
  flex-flow: row wrap;
  gap: 0.3rem 1rem;
  align-content: center;
  justify-content: flex start;
}

.content-title {
  color: var(--primary);
  font-weight: 600;
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.add-codebtor {
  margin-top: 1rem;
  margin-bottom: 1rem;
  width: 15rem;
  align-self: center;
}

.details-card {
  margin: 1.2rem;
}

.card-item-details {
  background-color: #F9F9F9;
  border-radius: 1.5rem;
  padding: 1rem;
  padding-left: 1.5rem;
  font-size: .9rem;
}

.eligibility-summary-reasons {
  width: 100%;
}
</style>

<i18n>
  {
    "cl": {
      "evaluationTitle": "Comprobación",
      "segmentText": "Segmento",
      "resultsButton": "Ver Resultados",
      "downloadButton": "Descargar",
      "refreshButton": "Calcular",
      "summary": "Resumen",
      "incomeSummary": "Renta del ",
      "debtSummary": "Deuda del ",
      "money": "($)",
      "debtor": "Deudor",
      "codebtor": "Coeudor",
      "incomeSummaryTotal": "Renta Total ($): ",
      "debtSummaryTotal": "Deuda Total ($): ",
      "incomeSummaryTotalUF": "Renta Total (UF): ",
      "incomeSummaryPercentageFixed": "Renta Fija (%): ",
      "incomeSummaryPercentageVariable": "Renta Variable (%): ",
      "eligibilitySummary": "Elegibilidad:",
      "summaryReasons": "Motivos:",
      "trueEligibilitySummary": "El cliente es elegible",
      "falseEligibilitySummary": "No es elegible",
      "ufText": "UF ($): ",
      "utmText": "UTM ($): ",
      "backButton": "Volver"
    },
    "pe": {
      "evaluationTitle": "Comprobación",
      "segmentText": "Segmento",
      "resultsButton": "Ver Resultados",
      "downloadButton": "Descargar",
      "refreshButton": "Calcular",
      "summary": "Resumen",
      "incomeSummary": "Renta del ",
      "debtSummary": "Deuda del ",
      "debtor": "Deudor",
      "codebtor": "Coeudor",
      "incomeSummaryTotal": "Renta Total (S/): ",
      "debtSummaryTotal": "Deuda Total (S/): ",
      "incomeSummaryTotalUF": "Renta Total (UF): ",
      "incomeSummaryPercentageFixed": "Renta Fija (%): ",
      "incomeSummaryPercentageVariable": "Renta Variable (%): ",
      "ufText": "UF (S/): ",
      "utmText": "UTM (S/): ",
      "backButton": "Volver"
    },
    "br": {
      "evaluationTitle": "Comprobación",
      "segmentText": "Segmento",
      "resultsButton": "Resultados",
      "downloadButton": "Download",
      "refreshButton": "Calcular",
      "summary": "Resumo",
      "incomeSummary": "Renda do ",
      "debtSummary": "Deuda do ",
      "debtor": "Deudor",
      "codebtor": "Coeudor",
      "incomeSummaryTotal": "Renda Total (R): ",
      "debtSummaryTotal": "Deuda Total (R): ",
      "incomeSummaryTotalUF": "Renda Total (UF): ",
      "incomeSummaryPercentageFixed": "Renda Fixa (%): ",
      "incomeSummaryPercentageVariable": "Renda Variavel (%): ",
      "ufText": "UF (R): ",
      "utmText": "UTM (R): ",
      "backButton": "Voltar"
    }
  }
  </i18n>
